import { Form } from 'antd';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { Service } from 'app/components/commons/Hotel/Services/Service/Service';
import 'app/components/commons/Hotel/Services/Services.scss';
import {
  addService,
  formatServicesForAPI,
  isServicesValid,
  removeService,
  updateService,
} from 'app/components/commons/Hotel/Services/utils';
import type { HotelServiceForm } from 'app/components/commons/Hotel/Types';
import { useHotelServicesTypes } from 'app/hooks/data/useHotelServicesTypes';

import { HeaderWrapper } from '../HeaderWrapper/HeaderWrapper';

import './ServicesForm.scss';

export const ServicesForm: FC<Props> = ({
  handleSubmit,
  initialValue,
  isLoading = false,
}) => {
  const [form] = Form.useForm();
  const { data: hotelsServicesTypes = [] } = useHotelServicesTypes();
  const [services, setServices] = useState<HotelServiceForm[]>(
    initialValue || []
  );
  const add = (service: HotelServiceForm) => addService(service, setServices);
  const update = (service: HotelServiceForm) =>
    updateService(service, setServices);
  const remove = (service: HotelServiceForm) =>
    removeService(service, setServices);

  useEffect(() => {
    if (initialValue) {
      setServices(initialValue);
    }
  }, [initialValue]);

  const formServices = Form.useWatch('services', form);

  const onFinish = useCallback(() => {
    const formattedServices = formatServicesForAPI(
      services,
      hotelsServicesTypes,
      formServices
    );

    handleSubmit(formattedServices as HotelServiceForm[]);
  }, [formServices, handleSubmit, services, hotelsServicesTypes]);

  const disabled = !isServicesValid(
    hotelsServicesTypes,
    services,
    formServices
  );

  return (
    <HeaderWrapper
      nextDisabled={disabled}
      handleNext={onFinish}
      isLoading={isLoading}
    >
      <div className="services-form">
        <div className="services-container">
          {hotelsServicesTypes.map((hotelsServicesType) => (
            <Service
              services={services}
              hotelsServicesType={hotelsServicesType}
              add={add}
              update={update}
              remove={remove}
              form={form}
              key={hotelsServicesType.id}
            />
          ))}
        </div>
      </div>
    </HeaderWrapper>
  );
};

type Props = {
  handleSubmit: (payload: HotelServiceForm[]) => void;
  initialValue: HotelServiceForm[];
  isLoading?: boolean;
};
